import { useCallback, useEffect, useState } from 'react';
import { useFetcher, useLocation } from 'react-router';
import type { BrandsByCategoryResult } from '@modules-rocco/professionals/api/get-brands-by-category.server';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    type CarouselApi,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-chevron-right.svg';
import { BrowseByBrandTile } from './BrowseByBrandTile';
import { useToggleFeaturedBrandPin } from '~/modules/home/hook/use-toggle-featured-brand-pin';
import { BRANDS_BY_CATEGORY_ACTION } from '~/routes/actions.brands-by-category';

/**
 * Displays a carousel of brands based on ElasticSearch aggregations.
 * Shows brands that sell products in the current category.
 * Fetches data client-side via useFetcher to avoid blocking server TTFB.
 *
 * - On /products root: Shows all brands
 * - On category pages: Shows only brands with products in that category
 */
export const BrowseByBrand = () => {
    const location = useLocation();
    const fetcher = useFetcher<BrandsByCategoryResult>();
    const [api, setApi] = useState<CarouselApi>();
    const [isScrolling, setIsScrolling] = useState(false);
    const [unpinnedIds, setUnpinnedIds] = useState<Set<number>>(new Set());
    // Single hook instance shared by all tiles (avoids 20 useFetcher instances)
    const { toggleFeaturedBrandPin } = useToggleFeaturedBrandPin();

    const handleUnpin = useCallback(
        (itemID: number, pin: boolean) => {
            toggleFeaturedBrandPin?.(itemID, pin);
            // Optimistically remove the tile from the carousel
            setUnpinnedIds((prev) => new Set(prev).add(itemID));
        },
        [toggleFeaturedBrandPin],
    );

    // Derive categoryLink from current pathname
    const basePath =
        '/' + location.pathname.split('/').filter(Boolean).join('/');
    const categoryLink = basePath !== '/products' ? basePath : undefined;

    useEffect(() => {
        setUnpinnedIds(new Set());
        const params = new URLSearchParams();
        if (categoryLink) {
            params.set('categoryLink', categoryLink);
        }
        fetcher.load(`${BRANDS_BY_CATEGORY_ACTION}?${params.toString()}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryLink]);

    // Disable pointer events during scroll to prevent hover state thrashing on Safari
    useEffect(() => {
        if (!api) return;
        const onScroll = () => setIsScrolling(true);
        const onSettle = () => setIsScrolling(false);
        api.on('scroll', onScroll);
        api.on('settle', onSettle);
        return () => {
            api.off('scroll', onScroll);
            api.off('settle', onSettle);
        };
    }, [api]);

    const allBrands = fetcher.data?.brands ?? [];
    const rawFeaturedCount = fetcher.data?.featuredCount ?? 0;
    const brands = allBrands.filter((b) => !unpinnedIds.has(b.id));
    const featuredCount = allBrands
        .slice(0, rawFeaturedCount)
        .filter((b) => !unpinnedIds.has(b.id)).length;
    const hasData = fetcher.data !== undefined;

    // Show skeleton until data arrives or while loading
    if (!hasData || fetcher.state === 'loading') {
        return <BrowseByBrandSkeleton />;
    }

    // Hide section if no brands after fetch completes
    if (brands.length === 0) {
        return null;
    }

    return (
        <section className="mb-20 md:mb-40">
            <Carousel
                setApi={setApi}
                options={{
                    slidesToScroll: 1,
                    align: 'start',
                    dragFree: true,
                    breakpoints: {
                        '(min-width: 768px)': { slidesToScroll: 4 },
                    },
                }}
            >
                {/* Header: Title + Navigation Arrows */}
                <div className="mb-5 flex items-center justify-between px-6 md:px-12">
                    <h2 className="text-5xl font-medium leading-[1.49] tracking-[-1.6px] text-[#141414] md:text-[5rem]">
                        Browse by Brand
                    </h2>
                    <div className="flex gap-2">
                        <CarouselPrevious
                            color="secondary"
                            size="square40"
                            className="bg-[#f2f2f2]"
                        >
                            <ArrowLeft className="size-[1.125rem]" />
                        </CarouselPrevious>
                        <CarouselNext
                            color="secondary"
                            size="square40"
                            className="bg-[#f2f2f2]"
                        >
                            <ArrowRight className="size-[1.125rem]" />
                        </CarouselNext>
                    </div>
                </div>

                {/* Carousel Content */}
                <CarouselContent
                    variant="fullPage"
                    className={isScrolling ? 'pointer-events-none' : ''}
                >
                    {brands.map((brand, index) => (
                        <CarouselItem
                            key={`browse-brand-${brand.id}`}
                            className="mr-4 max-w-[14.875rem] contain-content first:ml-0 last:mr-0"
                        >
                            <BrowseByBrandTile
                                professional={brand}
                                isFeatured={index < featuredCount}
                                onUnpin={
                                    toggleFeaturedBrandPin
                                        ? handleUnpin
                                        : undefined
                                }
                            />
                        </CarouselItem>
                    ))}
                </CarouselContent>
            </Carousel>
        </section>
    );
};

/**
 * Loading skeleton for the Browse by Brand section.
 */
const BrowseByBrandSkeleton = () => (
    <section className="mb-20 md:mb-40">
        <div className="px-6 md:px-12">
            <div className="mb-5 h-10 w-64 animate-pulse rounded bg-gray-200 md:h-[4rem]" />
            <div className="flex gap-4 overflow-hidden">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                        key={i}
                        className="aspect-238/347 w-[14.875rem] shrink-0 animate-pulse rounded-lg bg-gray-200"
                    />
                ))}
            </div>
        </div>
    </section>
);
